/**
 * UnifiedLeadCard — the GOLD STANDARD lead/prospect card used by Hunt, Farm, the Autonomous Stream and Prospect Discovery.
 * Composition: leads/leadModel (normalisation) + leads/useLeadCard (actions) + leads/LeadCardParts, LeadCompact, LeadExpandedDetails.
 */
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ChevronRight, ChevronDown, Send, ExternalLink } from "lucide-react";
import { VerificationBadges } from "@/components/VerificationBadges";
import { useLeadCard } from "./leads/useLeadCard";
import { LeadCompact } from "./leads/LeadCompact";
import { LeadExpandedDetails } from "./leads/LeadExpandedDetails";
import { CompanyFacts, InferredBuyer, IcpBadge, EmailPill, SaveButton, DismissButton, LeadFeedback, TitleLine, AccountThreads } from "./leads/LeadCardParts";

const stop = (fn) => (e) => { e.stopPropagation(); fn(); };

const UnifiedLeadCard = ({
  lead, index, userId, agentType = "hunt", actionId,
  onSave, onDismiss, onGenerateOutreach, onViewDetails, onIntelRefreshed,
  isSaved = false, isSaving = false, showActions = true, showDismiss = false, compact = false, isExpanded = false,
}) => {
  const card = useLeadCard({ lead, index, onIntelRefreshed });
  const { view } = card;

  if (compact) {
    return <LeadCompact lead={lead} index={index} userId={userId} agentType={agentType} actionId={actionId} view={view}
      onSave={onSave} onDismiss={onDismiss} onGenerateOutreach={onGenerateOutreach} onViewDetails={onViewDetails}
      isSaved={isSaved} isSaving={isSaving} showActions={showActions} showDismiss={showDismiss} />;
  }

  return (
    <motion.div
      className="glass-card-light rounded-xl p-4 border border-white/10 hover:border-ism-teal/30 transition-all"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      data-testid={`lead-card-${index}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-white font-medium">{view.name}</h3>
          <IcpBadge score={view.icpScore} color={view.icpColor} />
        </div>
        <div className="flex items-center gap-2">
          <LeadFeedback lead={lead} index={index} userId={userId} agentType={agentType} actionId={actionId} name={view.name} />
          {showDismiss && <DismissButton onClick={() => onDismiss?.(lead, index)} testId={`dismiss-lead-compact-btn-${index}`} />}
          <SaveButton onClick={() => onSave?.(lead, index)} isSaving={isSaving} isSaved={isSaved} testId={`save-lead-btn-${index}`} />
        </div>
      </div>

      <TitleLine title={view.title} company={view.company} />
      <VerificationBadges prospect={lead} idx={index ?? 0} />
      <CompanyFacts facts={view.companyFacts} registry={view.registry} index={index} />
      <AccountThreads lead={lead} index={index} userId={userId} />
      {view.isFirmOnly && <InferredBuyer buyer={lead.inferredBuyer} index={index} />}
      <EmailPill email={view.email} confidence={view.emailConfidence} />

      {view.linkedinUrl && (
        <a href={view.linkedinUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-ism-teal mb-3">
          <ExternalLink className="h-3 w-3" />
          View LinkedIn Profile
        </a>
      )}

      <AnimatePresence>{isExpanded && <LeadExpandedDetails lead={lead} view={view} actions={card} />}</AnimatePresence>

      {showActions && (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
          <Button onClick={stop(() => onViewDetails?.(lead, index))} size="sm" variant="outline"
            className={`text-xs border-white/20 hover:border-white/40 ${isExpanded ? "text-ism-teal border-ism-teal/30" : "text-gray-400 hover:text-white"}`}>
            {isExpanded ? <ChevronDown className="h-3 w-3 mr-1" /> : <ChevronRight className="h-3 w-3 mr-1" />}
            {isExpanded ? "Less Details" : "More Details"}
          </Button>
          <Button onClick={stop(() => onGenerateOutreach?.(lead, index))} size="sm" className="text-xs bg-ism-teal hover:bg-ism-teal/90 text-black">
            <Send className="h-3 w-3 mr-1" />
            Generate Outreach
          </Button>
        </div>
      )}
    </motion.div>
  );
};

export default UnifiedLeadCard;
